<script lang="ts">
import { watch, ref, computed } from "vue";
import DatePatternInput from "@/components/forms/DatePatternInput.vue";
import { useDateFormatting } from "@/composable/components/dateFormatting";

export default {
  name: "InputDateInterval",
  components: { DatePatternInput },
  props: {
    inputType: {
      type: String,
      default: "",
    },
    format: {
      type: String,
      default: "",
    },
    authorization: {
      type: Object,
      default: null,
    },
    hasExtraction: Boolean,
  },
  emits: ["update:date-value-interval", "update:sizeHeight", "update:validity"],
  setup(props, ctx) {
    let from = props.authorization?.fromDay;
    if (Array.isArray(from) && from.length === 3 && from[0] > 1900) {
      from[1] = from[1] - 1;
    }
    // from = formatting.formatted(from);
    let to = props.authorization?.toDay;
    if (Array.isArray(to) && to.length === 3 && to[0] > 1900) {
      to[1] = to[1] - 1;
    }
    // to = formatting.formatted(to);
    const fromValueDate = ref(from);
    const toValueDate = ref(to);
    watch(
      () => props.authorization,
      (value) => {
        fromValueDate.value = value?.fromDay;
        toValueDate.value = value?.toDay;
      },
      { immediate: true },
    );
    const local = getLocal();

    function getLocal() {
      if (props.format === "yyyy-MM-dd") {
        return "fr-CA";
      }
      else if (props.format === "MM/dd/yyyy") {
        return "en-US";
      }
      else if (props.format === "yyyy/MM/dd") {
        return "ja-JP";
      }
      else if (props.format === "dd.MM.yyyy") {
        return "ru-RU";
      }
      else {
        return "fr-FR";
      }
    }

    const isInvalid = ref(false);
    // #476 P2 - Regex du format date pour verifier la validite (pas seulement la longueur)
    const dateRegex = computed(() => useDateFormatting().parsePattern(props.format).regexp);
    // #476 P2 - Indique quel champ manque quand l'intervalle est incomplet
    const fromMissing = ref(false);
    const toMissing = ref(false);
    let validityTimer: ReturnType<typeof setTimeout> | null = null;

    // #476 - Appele au blur d'un champ date valide (via @input de DatePatternInput)
    function updateFromDate(event: string) {
      fromValueDate.value = event;
      emitIfValid();
    }

    function updateToDate(event: string) {
      toValueDate.value = event;
      emitIfValid();
    }

    // #476 - Verifie la coherence de l'intervalle et emet si valide
    function emitIfValid() {
      const fromStr = typeof fromValueDate.value === "string" ? fromValueDate.value : "";
      const toStr = typeof toValueDate.value === "string" ? toValueDate.value : "";
      // #476 P2 - Verifier la validite via regex (pas seulement la longueur)
      // Ex: "9999-99-99" a la bonne longueur mais ne matche pas la regex
      const fromComplete = fromStr.length === props.format.length && dateRegex.value.test(fromStr);
      const toComplete = toStr.length === props.format.length && dateRegex.value.test(toStr);

      // Les deux champs doivent etre complets
      // Si un seul est renseigne, indiquer quel champ manque
      fromMissing.value = !fromComplete && toComplete;
      toMissing.value = fromComplete && !toComplete;
      const bothEmpty = fromStr.length === 0 && toStr.length === 0;
      if (!fromComplete || !toComplete) {
        isInvalid.value = false;
        let reasonKey = "";
        if (fromMissing.value) reasonKey = "dataTypeAuthorizations.field-from-required";
        else if (toMissing.value) reasonKey = "dataTypeAuthorizations.field-to-required";
        else if (!bothEmpty) reasonKey = "rules.invalid-date-format";
        ctx.emit("update:validity", { valid: bothEmpty, reasonKey });
        return;
      }
      // Verifier la coherence De <= A
      if (fromStr > toStr) {
        isInvalid.value = true;
        ctx.emit("update:validity", {
          valid: false,
          reasonKey: "dataTypeAuthorizations.date-from-must-be-before-to",
        });
        return;
      }
      isInvalid.value = false;
      ctx.emit("update:validity", { valid: true });
      ctx.emit("update:date-value-interval", {
        interval: { from: fromValueDate.value, to: toValueDate.value, format: props.format },
        format: props.format,
        locale: local,
      });
    }

    // #476 - Validation de coherence en temps reel avec debounce (200ms)
    // Couvre la saisie clavier dans les champs date
    watch([fromValueDate, toValueDate], () => {
      if (validityTimer) clearTimeout(validityTimer);
      validityTimer = setTimeout(() => emitIfValid(), 200);
    });

    return {
      fromValueDate,
      toValueDate,
      updateFromDate,
      updateToDate,
      isInvalid,
      fromMissing,
      toMissing,
      local,
    };
  },
};
</script>

<template>
  <div class="rows">
    <div class="row">
      <span
        class="label"
        :aria-label="$t('dataTypeAuthorizations.from')"
      >{{ $t("dataTypeAuthorizations.from") }}</span>
    </div>
    <div
      class="row"
      :class="{ 'has-error': fromMissing }"
    >
      <!-- #476 P3 - isRequired=false car les dates sont optionnelles dans les filtres -->
      <DatePatternInput
        v-model="fromValueDate"
        aria-hidden="false"
        :pattern="format"
        :is-required="false"
        :disabled="!hasExtraction"
        type="startDate"
        @input="updateFromDate($event)"
      />
    </div>
    <div class="row">
      <span
        class="label"
        :aria-label="$t('dataTypeAuthorizations.to')"
      >{{ $t("dataTypeAuthorizations.to") }}</span>
    </div>
    <div
      class="row"
      :class="{ 'has-error': toMissing }"
    >
      <DatePatternInput
        v-model="toValueDate"
        aria-hidden="false"
        :pattern="format"
        :is-required="false"
        :disabled="!hasExtraction"
        type="endDate"
        @input="updateToDate($event)"
      />
    </div>
    <p
      v-if="fromMissing"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.field-from-required") }}
    </p>
    <p
      v-if="toMissing"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.field-to-required") }}
    </p>
    <p
      v-if="isInvalid"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.date-from-must-be-before-to") }}
    </p>
  </div>
</template>

<style scoped>
/* #476 P2 - Encadrer le champ en rouge quand la date est manquante */
.has-error :deep(input) {
  border-color: #ff3860;
}
</style>
